import { useState, useEffect, useRef } from 'react'
import { getPublicParticipant } from '../utils/api'
import ProfileResult, { C, font, GlassPanel } from './ProfileResult'
import Mindmap from './Mindmap'
import VisitorChat from './VisitorChat'

// Inline design tokens (no circular imports)
const bg = '#05050d'
const surface = 'rgba(255,255,255,0.028)'
const border = 'rgba(180,185,230,0.09)'
const accent = '#b8bdd4'
const textMuted = 'rgba(180,185,212,0.5)'

// ── Visibility toggle component ───────────────────────────────────────────────
const VISIBILITY_LABELS = {
  show_personality: 'Личность',
  show_competencies: 'Компетенции',
  show_career: 'Карьера',
  show_motivation: 'Мотивация',
  show_mindmap: 'Mindmap',
  show_natal: 'Натальная карта',
  show_facts: 'Факты',
  show_chat: 'Чат с двойником',
  show_music: 'Музыка',
}

export function VisibilitySettings({ settings, onChange, saving }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {Object.entries(VISIBILITY_LABELS).map(([key, label]) => {
        const on = settings[key] !== false
        return (
          <button
            key={key}
            onClick={() => onChange({ ...settings, [key]: !on })}
            disabled={saving}
            style={{
              padding: '4px 12px',
              borderRadius: 20,
              border: `1px solid ${on ? 'rgba(120,200,120,0.4)' : border}`,
              background: on ? 'rgba(100,180,100,0.08)' : 'transparent',
              color: on ? 'rgba(140,210,140,0.9)' : textMuted,
              fontFamily: font.mono,
              fontSize: 11,
              cursor: saving ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {on ? '✓' : '○'} {label}
          </button>
        )
      })}
    </div>
  )
}

// ── Main public page ──────────────────────────────────────────────────────────
export default function PublicParticipantPage({ participantId }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState('personality')
  const scrollRef = useRef(null)

  // Determine auth level
  const isOwner = localStorage.getItem('dt_owner') === 'true'
  const myParticipantId = localStorage.getItem('dt_participant_id') || localStorage.getItem('dt_owner_participant_id')
  const isThisParticipant = String(myParticipantId) === String(participantId)
  const canSeeAll = isOwner || isThisParticipant

  useEffect(() => {
    getPublicParticipant(participantId)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }, [participantId])

  if (loading) return (
    <div style={{ minHeight: '100vh', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: textMuted, fontFamily: font.mono, fontSize: 13 }}>загрузка профиля...</div>
    </div>
  )

  if (error || !data) return (
    <div style={{ minHeight: '100vh', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: 'rgba(200,80,80,0.8)', fontFamily: font.mono, fontSize: 13 }}>
        {error || 'профиль не найден'}
      </div>
    </div>
  )

  if (!data.profile) return (
    <div style={{ minHeight: '100vh', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontFamily: font.serif, fontSize: 28, color: accent, marginBottom: 8 }}>
          {data.display_name}
        </div>
        <div style={{ color: textMuted, fontFamily: font.mono, fontSize: 12 }}>профиль ещё не создан</div>
      </div>
    </div>
  )

  const s = data.public_settings
  const profile = data.profile

  // Build tabs based on visibility + what's available
  const tabs = [
    s.show_personality && profile.personality_data && { id: 'personality', label: 'Личность' },
    s.show_competencies && profile.competencies_data && { id: 'competencies', label: 'Компетенции' },
    s.show_career && profile.career_data && { id: 'career', label: 'Карьера' },
    s.show_motivation && profile.motivation_data && { id: 'motivation', label: 'Мотивация' },
    s.show_mindmap && { id: 'mindmap', label: 'Mindmap' },
    s.show_chat && { id: 'chat', label: 'Чат' },
  ].filter(Boolean)

  // If current tab got hidden, switch to first available
  const validTab = tabs.find(t => t.id === activeTab) ? activeTab : (tabs[0]?.id || 'personality')

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Space+Mono:wght@400;700&family=Inter:wght@300;400;500;600&display=swap" rel="stylesheet" />
      <style>{`*,*::before,*::after{box-sizing:border-box}html,body{margin:0;padding:0;height:100%;background:${bg};overflow:hidden}#root{height:100vh;display:flex;flex-direction:column}::-webkit-scrollbar{width:3px}::-webkit-scrollbar-thumb{background:rgba(180,185,230,0.15);border-radius:2px}`}</style>

      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: bg, position: 'relative' }}>
        {/* Star background */}
        <div style={{ backgroundImage: 'url(/stars.svg)', backgroundSize: 'cover', position: 'fixed', inset: 0, opacity: 0.6, zIndex: 0, pointerEvents: 'none' }} />

        {/* Header */}
        <div style={{
          position: 'relative', zIndex: 10,
          padding: '16px 24px 0',
          borderBottom: `1px solid ${border}`,
          backdropFilter: 'blur(12px)',
          background: 'rgba(5,5,13,0.85)',
        }}>
          {/* Top row: name + back link */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <a
                href="/"
                style={{ fontFamily: font.mono, fontSize: 11, color: textMuted, textDecoration: 'none', opacity: 0.7 }}
              >
                ◈ Digital Twin
              </a>
              <span style={{ color: border }}>·</span>
              <span style={{ fontFamily: font.serif, fontSize: 22, color: accent, letterSpacing: '0.02em' }}>
                {data.display_name}
              </span>
            </div>
            {canSeeAll && (
              <span style={{
                fontFamily: font.mono, fontSize: 10, color: 'rgba(140,200,140,0.7)',
                padding: '3px 8px', borderRadius: 10, border: '1px solid rgba(140,200,140,0.2)',
                background: 'rgba(100,180,100,0.06)',
              }}>
                {isOwner ? 'владелец' : 'участник'}
              </span>
            )}
          </div>

          {/* Tabs */}
          {tabs.length > 0 && (
            <div style={{ display: 'flex', gap: 0, overflowX: 'auto' }}>
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id); scrollRef.current?.scrollTo(0, 0) }}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: '8px 16px',
                    fontFamily: font.mono, fontSize: 12,
                    color: validTab === tab.id ? accent : textMuted,
                    borderBottom: validTab === tab.id ? `1px solid ${accent}` : '1px solid transparent',
                    whiteSpace: 'nowrap',
                    transition: 'color 0.15s',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div
          ref={scrollRef}
          style={{
            flex: 1,
            overflowY: validTab === 'mindmap' ? 'hidden' : 'auto',
            position: 'relative', zIndex: 1,
            padding: validTab === 'mindmap' ? 0 : '24px 24px 60px',
          }}
        >
          {validTab === 'personality' && profile.personality_data && (
            <ProfileResult
              profile={profile}
              activeTab="personality"
              isOwner={false}
              showEvidence={canSeeAll}
            />
          )}
          {validTab === 'competencies' && profile.competencies_data && (
            <ProfileResult
              profile={profile}
              activeTab="competencies"
              isOwner={false}
              showEvidence={canSeeAll}
            />
          )}
          {validTab === 'career' && profile.career_data && (
            <ProfileResult
              profile={profile}
              activeTab="career"
              isOwner={false}
              showEvidence={canSeeAll}
            />
          )}
          {validTab === 'motivation' && profile.motivation_data && (
            <ProfileResult
              profile={profile}
              activeTab="motivation"
              isOwner={false}
              showEvidence={canSeeAll}
            />
          )}
          {validTab === 'mindmap' && (
            <div style={{ height: '100%' }}>
              <Mindmap profile={profile} isOwner={false} />
            </div>
          )}
          {validTab === 'chat' && (
            <div style={{ maxWidth: 680, margin: '0 auto' }}>
              <VisitorChat profileUserId={`participant_${participantId}`} />
            </div>
          )}
        </div>
      </div>
    </>
  )
}
